import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/layout';
import { ChevronRight, Loader2, Tag, Sparkles, FolderTree } from 'lucide-react';
import SignatureBlock from '../components/SignatureBlock';
import axios from 'axios';
import { toAbsoluteUrl } from '../lib/utils';
import homebloc2 from '../assets/images/home-bloc2.png';
import logoDsrs from '../assets/logos/logodsrs.png';


const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service Card Component
function ServiceCard({ service, index }) {
  const isPromo = service.is_promo && service.promo_price;
  const imageUrl = service.no_photo ? null : toAbsoluteUrl(service.image_url || service.image || service.photo);
  const fallbackSrc = imageUrl || logoDsrs;
  const priceLabel = isPromo ? service.promo_price?.toFixed(2) : service.price?.toFixed(2);
  const durationLabel = service.duration_minutes ? `${service.duration_minutes} MIN` : null;
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionStyle = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: isExpanded ? 'unset' : 3,
    overflow: isExpanded ? 'visible' : 'hidden',
    textOverflow: 'ellipsis'
  };
  
  return (
    <div
      id={`service-${service.id}`}
      data-testid={`service-item-${index}`}
      className={`group bg-white border transition-all duration-300 flex flex-col md:flex-row items-stretch ${
        !isExpanded ? 'max-h-[300px] overflow-hidden md:h-[384px]' : 'overflow-visible md:h-auto'
      } ${
        isPromo
          ? 'border-green-200 bg-green-50/30 hover:border-green-300'
          : 'border-[var(--champagne)] hover:border-[var(--gold)]/30'
      } shadow-[0_10px_30px_rgba(0,0,0,0.05)] hover:-translate-y-1`}
    >
      <div className={`md:w-[40%] aspect-[5/4] md:aspect-auto bg-[var(--champagne)]/5 overflow-hidden relative ${
        isExpanded ? 'md:min-h-[384px]' : 'md:h-full'
      }`}>
        {isExpanded && (
          <img
            src={fallbackSrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-40"
          />
        )}
        <img
          src={fallbackSrc}
          alt={service.name}
          className={
            isExpanded
              ? "relative w-full h-full object-contain p-4"
              : "relative w-full h-full object-cover"
          }
          onError={(e) => {
            e.currentTarget.src = logoDsrs;
          }}
        />
      </div>

      <div className="flex-1 px-6 py-6 md:px-10 md:py-8 flex flex-col">
        <div className="border-b border-[var(--champagne)]/40 pb-4 mb-5">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-[26px] text-[var(--charcoal)] uppercase tracking-[2px]">
              {service.name}
            </h3>
            {isPromo && (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs uppercase tracking-wider flex items-center gap-1">
                <Tag size={12} />
                Promo
              </span>
            )}
          </div>
          <div className="text-[13px] text-[var(--gold)] uppercase tracking-[1.5px] mt-2">
            {[durationLabel, priceLabel && `${priceLabel} €`].filter(Boolean).join(' • ')}
          </div>
        </div>
        <p
          className="text-sm leading-[1.8] text-[#666] mb-4 font-light"
          style={descriptionStyle}
        >
          {service.description}
        </p>
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="text-[11px] uppercase tracking-[2px] text-[var(--gold)] border-none bg-transparent p-0 mb-6"
        >
          {isExpanded ? '- VOIR MOINS' : '+ VOIR PLUS'}
        </button>
        <div className="flex items-center justify-between">
          <a
            href={process.env.REACT_APP_PLANITY_URL || 'https://www.planity.com/madame-dsrs-95570-attainville'}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-[var(--gold)] text-[var(--gold)] px-6 py-3 text-[11px] uppercase tracking-[2px] font-medium transition-all duration-300 hover:bg-[var(--gold)] hover:text-white"
          >
            Réserver ce soin
          </a>
          {isPromo && service.price && (
            <span className="text-[var(--charcoal)]/40 line-through text-xs">
              {service.price.toFixed(2)}€
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceGridWithSubcategories({ services, subcategories }) {
  // Get bien_etre subcategories
  const bienEtreSubcats = subcategories.filter(s => s.parent_category === 'bien_etre');
  
  // Services without subcategory
  const uncategorized = services.filter(s => !s.subcategory_id);
  
  // Services grouped by subcategory
  const grouped = bienEtreSubcats
    .filter(subcat => services.some(s => s.subcategory_id === subcat.id))
    .map(subcat => ({
      subcategory: subcat,
      services: services.filter(s => s.subcategory_id === subcat.id)
    }));

  // If no subcategories with services, just show all services
  if (grouped.length === 0) {
    return (
      <StaggerContainer className="space-y-4">
        {services.map((service, index) => (
          <StaggerItem key={service.id}>
            <ServiceCard service={service} index={index} />
          </StaggerItem>
        ))}
      </StaggerContainer>
    );
  }

  return (
    <div className="space-y-10">
      {/* Grouped by subcategory */}
      {grouped.map(group => (
        <div key={group.subcategory.id}>
          <div className="flex items-center gap-3 mb-4 pb-2 border-b border-[var(--champagne)]">
            <FolderTree size={18} className="text-[var(--gold)]" />
            <h3 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
              {group.subcategory.name}
            </h3>
            <span className="text-sm text-[var(--charcoal)]/40">
              ({group.services.length} soin{group.services.length > 1 ? 's' : ''})
            </span>
          </div>
          <StaggerContainer className="space-y-4">
            {group.services.map((service, index) => (
              <StaggerItem key={service.id}>
                <ServiceCard service={service} index={index} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      ))}

      {/* Uncategorized services */}
      {uncategorized.length > 0 && (
        <div>
          {grouped.length > 0 && (
            <div className="flex items-center gap-3 mb-4 pb-2 border-b border-[var(--champagne)]">
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[var(--charcoal)]">
                Autres soins
              </h3>
              <span className="text-sm text-[var(--charcoal)]/40">
                ({uncategorized.length})
              </span>
            </div>
          )}
          <StaggerContainer className="space-y-4">
            {uncategorized.map((service, index) => (
              <StaggerItem key={service.id}>
                <ServiceCard service={service} index={index} />
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      )}
    </div>
  );
}

export default function BienEtrePage() {
  const [services, setServices] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch bien_etre, madero categories and subcategories
        const [bienEtreRes, maderoRes, subcatRes] = await Promise.all([
          axios.get(`${API_URL}/services/category/bien_etre`),
          axios.get(`${API_URL}/services/category/madero`),
          axios.get(`${API_URL}/services/subcategories`)
        ]);
        setServices([...bienEtreRes.data, ...maderoRes.data]);
        setSubcategories(subcatRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isLoading || !location.hash) {
      return;
    }
    const targetId = location.hash.replace('#', '');
    if (!targetId) {
      return;
    }
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      setTimeout(() => {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [isLoading, location.hash, services]);

  return (
    <PageTransition>
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden" data-testid="bienetre-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${homebloc2})`,
            filter: 'brightness(0.5)'
          }}
        />
        <div className="relative z-10 text-center text-white px-6">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs uppercase tracking-[0.4em] text-[var(--rose-gold)] mb-4"
          >
            Nos Soins
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light mb-4"
          >
            Bien-être
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-white/80 max-w-xl mx-auto"
          >
            Massages et soins relaxants pour votre corps et votre esprit
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)] transition-colors">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Bien-être</span>
          </nav>
        </div>
      </div>

      {/* Signature Block - Featured Services */}
      <SignatureBlock
        categoryType="bien_etre"
        title="Nos Soins Signature"
        subtitle="Découvrez nos soins phares pour un moment de détente absolue et de relaxation profonde."
        ctaLink="/bien-etre"
        ctaLabel="Réserver ce soin"
      />

      {/* Gift Card Banner */}
      <section className="py-12 px-6 bg-gradient-to-r from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-3">
              Idée Cadeau
            </p>
            <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-[var(--charcoal)] mb-4">
              Offrez un moment de détente
            </h3>
            <p className="text-sm text-[var(--charcoal)]/70 mb-6 max-w-md mx-auto">
              Faites plaisir à vos proches avec une carte cadeau valable sur tous nos soins bien-être
            </p>
            <Link
              to="/carte-cadeau"
              className="inline-block px-8 py-3 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
              data-testid="giftcard-banner-btn"
            >
              Offrir une carte cadeau
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-[var(--champagne)]/10" data-testid="bienetre-content">
        <div className="max-w-[1200px] mx-auto">
          <FadeIn className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--dark-rosegold)] mb-4">
              Tous nos soins
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[var(--charcoal)] mb-4">
              Détente & Relaxation
            </h2>
            <p className="text-[var(--charcoal)]/60 max-w-2xl mx-auto">
              Découvrez notre gamme complète de soins bien-être : massages, drainages et techniques de relaxation pour un moment de détente absolue.
            </p>
          </FadeIn>

          {isLoading ? (
            <div className="text-center py-12">
              <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--gold)]" />
              <p className="text-sm text-[var(--charcoal)]/60">Chargement des prestations...</p>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-12 bg-white border border-[var(--champagne)]">
              <Sparkles size={48} className="mx-auto mb-4 text-[var(--charcoal)]/20" />
              <p className="text-[var(--charcoal)]/60">Aucune prestation disponible pour le moment.</p>
            </div>
          ) : (
            <ServiceGridWithSubcategories services={services} subcategories={subcategories} />
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[var(--charcoal)]" data-testid="cta-section">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl text-white mb-6">
              Offrez-vous un moment de détente
            </h2>
            <p className="text-white/70 mb-8">
              Réservez votre séance bien-être et laissez-vous transporter.
            </p>
            <a
              href={process.env.REACT_APP_PLANITY_URL || 'https://www.planity.com/madame-dsrs-95570-attainville'}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bienetre-cta-planity"
              className="inline-block px-10 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors duration-300"
            >
              Prendre rendez-vous
            </a>
          </FadeIn>
        </div>
      </section>
    </PageTransition>
  );
}
